// Compact, prompt-ready rendering of a forecast's REPORT — the summary, the top drivers for
// and against, and the sources. The chat classifier previously saw only the question,
// probability, and a thin trigger list, so it could not answer "why is it 63%?", "what's the
// strongest Yes driver?", or "what sources?" — the reasoning simply wasn't in context. The
// material is already on the completed run (`active.report`, else `serverContext.report`), so
// this is a pure read (no React, no store) that the classifier context assembles from.
//
// WHY THE CAPS ARE GENEROUS NOW. The first version clipped hard (600-char summary, 3 reasons
// a side, no criteria / aggregation / calibration) to keep the classifier prompt near 2k
// tokens. That clip was the reason Sayer could describe a forecast but never reason about it:
// a production report carries >=10 reasons per side (each strength-rated, each with
// {source_url, quote} citations), the resolution-criteria breakdown, 4-8 weighing
// observations, and the initial -> calibrated probability walk — and the modal renders ALL of
// it while the chat saw a sliver. The caps below are sized to admit a whole real report
// (~4-6k tokens) while still BOUNDING it: report size varies a lot (a 20-reason report is
// twice a 10-reason one), and an uncapped block would make prompt cost a function of whatever
// the forecaster happened to emit. Every cap is a defensive ceiling, not a target — a typical
// report lands well inside them.
//
// Citations are rendered as bare URLs, never with their `quote`. The quotes are the bulk of a
// grounded report and the model does not need them to say which source backs a claim.

import { clip, fmtItems } from "./promptLines.js";
import { sideChanceText } from "./formatPct.js";

/**
 * @typedef {object} ForecastReportContext
 * @property {string} summary        the assessment paragraph (forecast_summary), or ""
 * @property {string} rephrased      the forecaster's precise restatement of the question, or ""
 * @property {string} criteria       "  - condition" lines + a synthesis line, or ""
 * @property {string} reasonsFor     "  - [strong] claim (url)" lines, or ""
 * @property {string} reasonsAgainst same shape as reasonsFor, or ""
 * @property {string} aggregation    "  - observation" lines weighing the two sides, or ""
 * @property {string} calibration    the initial -> final probability walk, or ""
 * @property {string} evidence       background-evidence lines (report.summaries), or ""
 * @property {string} sources        "  - title url" lines, or ""
 */

/**
 * Build the report context strings from a run. Reads `run.report` (top-level, the fresh
 * shape) then `run.serverContext.report` (the reload shape); sources fall back to the
 * contract's own `sources`. Every field degrades to "" when absent — a multi run, or a
 * binary forecast whose report step failed, simply contributes nothing here.
 * @param {any} run a store run (the "active" forecast), or null
 * @param {{ maxReasons?: number, maxSources?: number, claimLen?: number,
 *           maxCriteria?: number, maxAggregation?: number, maxEvidence?: number,
 *           summaryLen?: number }} [opts]
 * @returns {ForecastReportContext}
 */
export function forecastReportContext(
  run,
  {
    maxReasons = 12,
    maxSources = 12,
    claimLen = 400,
    maxCriteria = 8,
    maxAggregation = 8,
    maxEvidence = 6,
    summaryLen = 2000,
  } = {},
) {
  const contract = run?.contract || {};
  const report = run?.report || run?.serverContext?.report || {};
  const rca = report.resolution_criteria_analysis || null;

  // The criteria breakdown reads as one block: the conditions, then what the forecaster
  // concluded binds vs. is interpretive. Joined here (not two fields) because the synthesis
  // line is meaningless without the conditions above it.
  let criteria = fmtItems(rca?.criteria, {
    max: maxCriteria,
    len: claimLen,
    cites: 1,
    pick: (c) => c?.statement ?? c,
  });
  if (rca?.summary) {
    const synth = clip(rca.summary, claimLen);
    if (synth) criteria = criteria ? `${criteria}\n  Synthesis: ${synth}` : `  ${synth}`;
  }

  // The calibration walk is two fields that only mean something together: the pre-calibration
  // stance and the prose explaining the move to the final number. Emitted as one line + body
  // so a model asked "how confident is this?" sees the adjustment, not just the endpoint.
  const initial =
    typeof report.initial_probability === "number" ? report.initial_probability : null;
  const calibrationBody = clip(report.calibration_analysis, 1200);
  const calibration = calibrationBody
    ? initial === null
      ? calibrationBody
      : `Initial reading before calibration: ${sideChanceText(initial)}. ${calibrationBody}`
    : "";

  const srcArr =
    (Array.isArray(report.sources) && report.sources.length ? report.sources : contract.sources) ||
    [];
  const sources = srcArr
    .map((s) => {
      if (typeof s === "string") return clip(s, 200);
      const title = clip(s?.title ?? s?.name ?? "", 120);
      const url = String(s?.url ?? s?.source_url ?? "").trim();
      return [title, url].filter(Boolean).join(" ");
    })
    .filter(Boolean)
    .slice(0, maxSources)
    .map((s) => `  - ${s}`)
    .join("\n");

  return {
    summary: clip(report.forecast_summary, summaryLen),
    rephrased: clip(report.rephrased_question, 600),
    criteria,
    reasonsFor: fmtItems(report.reasons_for, {
      max: maxReasons,
      len: claimLen,
      strength: true,
      cites: 2,
    }),
    reasonsAgainst: fmtItems(report.reasons_against, {
      max: maxReasons,
      len: claimLen,
      strength: true,
      cites: 2,
    }),
    aggregation: fmtItems(report.aggregation, { max: maxAggregation, len: claimLen }),
    calibration,
    evidence: fmtItems(report.summaries, { max: maxEvidence, len: claimLen, cites: 1 }),
    sources,
  };
}
